//
// # PathField
//
// Shared labeled text input used by the create (DesignNameDialog) and Save As
// (SaveAsDialog) dialogs for the design's parent path and leaf name. The caller
// owns validation and supplies the label, placeholder, helper/error message and
// whether to flag the border as invalid.
//
// Styled with the command-palette `add-instance-param-*` classes so it sits
// inside the anchored-at-top palette dialogs consistently (see IDE AGENTS.md:
// dialogs use the command-palette style, not the floating Modal).
//

import React from "react";

// Shared error text for a malformed parent path. Exported so callers can use it
// in their own message logic without duplicating the wording.
export const PATH_FORMAT_ERROR =
  "Slash-separated path; each part: letters, numbers, underscores, and hyphens only";

// Shared error text for a malformed single segment (the leaf name).
export const SEGMENT_FORMAT_ERROR =
  "Letters, numbers, underscores, and hyphens only";

export interface PathFieldMessage {
  readonly text: string;
  // When true the message is rendered in the error color, otherwise dimmed.
  readonly isError: boolean;
}

export interface PathFieldProps {
  readonly label: string;
  readonly placeholder: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onBlur: () => void;
  // Render the input border in the error color (caller folds in "touched").
  readonly invalid: boolean;
  // Helper or error text shown below the input.
  readonly message: PathFieldMessage;
  // Focus this field on mount. Only one field per dialog should set this.
  readonly autoFocus?: boolean;
}

export function PathField({
  label,
  placeholder,
  value,
  onChange,
  onBlur,
  invalid,
  message,
  autoFocus = false,
}: PathFieldProps) {
  return (
    <div className="add-instance-param-field">
      <label className="add-instance-param-label">{label}</label>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        onBlur={onBlur}
        className={
          invalid
            ? "add-instance-param-input add-instance-param-input-error"
            : "add-instance-param-input"
        }
        autoFocus={autoFocus}
        autoComplete="off"
        spellCheck={false}
      />
      <span
        className={
          message.isError
            ? "add-instance-param-error"
            : "add-instance-param-constraint"
        }
      >
        {message.text}
      </span>
    </div>
  );
}
